/** The side of its anchor a popover sits on. */
export type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right';

/** The parts of a `DOMRect` the positioning reads. */
export type PopoverRect = Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom' | 'width' | 'height'>;

export interface PopoverViewport {
    width: number;
    height: number;
}

export interface PopoverPosition {
    top: number;
    left: number;
    /** The side actually used: the requested one, or its opposite when it didn't fit. */
    placement: PopoverPlacement;
    /**
     * Distance in px from the popover box's top-left to where the arrow sits,
     * along the side it points from, so it keeps pointing at the anchor's
     * center even when the box was shifted to stay on-screen.
     */
    arrowOffset: number;
}

/** Space between the anchor and the popover. */
const POPOVER_GAP = 8;
/** Minimum space between the popover and the viewport's edges. */
const VIEWPORT_PADDING = 8;
/** Keeps the arrow clear of the popover's rounded corners when it's pinned near an edge. */
const ARROW_EDGE_MARGIN = 12;

const OPPOSITE_PLACEMENT: Record<PopoverPlacement, PopoverPlacement> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
};

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
}

/** Whether the popover fits between the anchor and the viewport edge on `placement`'s side. */
function canFitOnSide(placement: PopoverPlacement, anchorRect: PopoverRect, popoverRect: PopoverRect, viewport: PopoverViewport): boolean {
    switch (placement) {
        case 'top':
            return anchorRect.top - VIEWPORT_PADDING >= popoverRect.height + POPOVER_GAP;
        case 'bottom':
            return viewport.height - anchorRect.bottom - VIEWPORT_PADDING >= popoverRect.height + POPOVER_GAP;
        case 'left':
            return anchorRect.left - VIEWPORT_PADDING >= popoverRect.width + POPOVER_GAP;
        case 'right':
            return viewport.width - anchorRect.right - VIEWPORT_PADDING >= popoverRect.width + POPOVER_GAP;
    }
}

/**
 * Where a popover goes beside its anchor: on the requested side, flipped to
 * the opposite one when it doesn't fit there, then kept inside the viewport,
 * with the arrow still pointing at the anchor's center (but clear of the
 * box's corners).
 */
export function resolvePopoverPosition(
    anchorRect: PopoverRect,
    popoverRect: PopoverRect,
    requestedPlacement: PopoverPlacement,
    viewport: PopoverViewport,
): PopoverPosition {
    const placement = canFitOnSide(requestedPlacement, anchorRect, popoverRect, viewport)
        ? requestedPlacement
        : OPPOSITE_PLACEMENT[requestedPlacement];
    const anchorCenterX = anchorRect.left + anchorRect.width / 2;
    const anchorCenterY = anchorRect.top + anchorRect.height / 2;

    let top: number;
    let left: number;
    switch (placement) {
        case 'top':
            top = anchorRect.top - popoverRect.height - POPOVER_GAP;
            left = anchorCenterX - popoverRect.width / 2;
            break;
        case 'bottom':
            top = anchorRect.bottom + POPOVER_GAP;
            left = anchorCenterX - popoverRect.width / 2;
            break;
        case 'left':
            top = anchorCenterY - popoverRect.height / 2;
            left = anchorRect.left - popoverRect.width - POPOVER_GAP;
            break;
        case 'right':
            top = anchorCenterY - popoverRect.height / 2;
            left = anchorRect.right + POPOVER_GAP;
            break;
    }

    left = clamp(left, VIEWPORT_PADDING, viewport.width - popoverRect.width - VIEWPORT_PADDING);
    top = clamp(top, VIEWPORT_PADDING, viewport.height - popoverRect.height - VIEWPORT_PADDING);

    const arrowOffset = placement === 'top' || placement === 'bottom'
        ? clamp(anchorCenterX - left, ARROW_EDGE_MARGIN, popoverRect.width - ARROW_EDGE_MARGIN)
        : clamp(anchorCenterY - top, ARROW_EDGE_MARGIN, popoverRect.height - ARROW_EDGE_MARGIN);

    return { top, left, placement, arrowOffset };
}
